<script>
  import { currentScreen, availableTeams } from '../store.js';
  import { resizeImageDataUrl } from '../../store.js';
  import { toast, confirmDialog } from '../../ui/dialogs.js';
  import { onMount } from 'svelte';

  let customTeams = [];
  let view = 'list'; // 'list', 'create', 'edit'
  
  // Form fields
  let editId = null; // null for new team
  let name = "";
  let captain = "";
  let color = "#3b82f6";
  let logo = "";
  let players = new Array(8).fill(""); // default 8 slots

  onMount(() => {
    loadCustomTeams();
  });

  function loadCustomTeams() {
    try {
      customTeams = JSON.parse(localStorage.getItem('cs_custom_teams_v1') || "[]");
    } catch (_) {
      customTeams = [];
    }
  }

  function goBack() {
    if (view !== 'list') {
      view = 'list';
      resetForm();
    } else {
      currentScreen.set('home');
    }
  }

  function resetForm() {
    editId = null;
    name = "";
    captain = "";
    color = "#3b82f6";
    logo = "";
    players = new Array(8).fill("");
  }

  function addPlayerSlot() {
    players = [...players, ""];
  }

  function removePlayerSlot(index) {
    if (players.length > 2) {
      players = players.filter((_, i) => i !== index);
    }
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result;
      try {
        // Resize logo to max 128px to keep localStorage small
        logo = await resizeImageDataUrl(base64, 128);
      } catch (err) {
        logo = base64; // fallback
      }
    };
    reader.readAsDataURL(file);
  }

  function saveTeam() {
    if (!name.trim()) {
      toast("Please enter team name.", "error");
      return;
    }
    if (!captain.trim()) {
      toast("Please enter captain name.", "error");
      return;
    }

    const cap = captain.trim();
    const filteredPlayers = players.map(p => p.trim()).filter(Boolean);
    // BUGFIX: the captain input is separate from the roster, so a user could
    // set captain "Rohit" while the players list never contains him — then the
    // 👑 shown a non-playing name and the captain was missing from the XI.
    // Guarantee the captain is in the squad and listed first.
    const roster = [cap, ...filteredPlayers.filter(p => p.toLowerCase() !== cap.toLowerCase())];
    if (roster.length < 2) {
      toast("Please add at least 2 players to the team.", "error");
      return;
    }

    const teamData = {
      id: editId || "team_" + Date.now(),
      name: name.trim(),
      captain: cap,
      color,
      logo: logo || "/logo-placeholder.png",
      players: roster
    };

    let updated = [...customTeams];
    if (editId) {
      // Edit mode
      updated = updated.map(t => t.id === editId ? teamData : t);
    } else {
      // Create mode
      if (customTeams.some(t => t.name.toLowerCase() === teamData.name.toLowerCase())) {
        toast("A team with this name already exists.", "error");
        return;
      }
      updated.push(teamData);
    }

    customTeams = updated;
    localStorage.setItem('cs_custom_teams_v1', JSON.stringify(customTeams));
    
    // Sync Svelte store
    updateAvailableTeamsStore();

    view = 'list';
    resetForm();
    toast("🛡️ Team saved successfully!", "success");
  }

  function editTeam(team) {
    editId = team.id;
    name = team.name;
    captain = team.captain;
    color = team.color;
    logo = team.logo;
    
    // Pad players list to at least 8 elements for convenience
    let loadedPlayers = [...(team.players || [])];
    while (loadedPlayers.length < 8) {
      loadedPlayers.push("");
    }
    players = loadedPlayers;
    view = 'edit';
  }

  async function deleteTeam(id) {
    if (await confirmDialog("Delete this team? Roster and meta info will be removed permanently.", { title: "Delete team", danger: true })) {
      customTeams = customTeams.filter(t => t.id !== id);
      localStorage.setItem('cs_custom_teams_v1', JSON.stringify(customTeams));
      updateAvailableTeamsStore();
    }
  }

  function updateAvailableTeamsStore() {
    // Import hardcoded ones dynamically from storage helper structure
    import('../store.js').then(({ getMeta }) => {
      import('../../teams-data.js').then(({ teams: ffplTeams }) => {
        const ffpl = ffplTeams.map(t => {
          const override = getMeta(t.name);
          return { ...t, ...override };
        });

        const merged = [...customTeams];
        const customNames = new Set(customTeams.map(t => t.name));
        
        ffpl.forEach(t => {
          if (!customNames.has(t.name)) {
            merged.push(t);
          }
        });
        availableTeams.set(merged);
      });
    });
  }

  const presetColors = ["#ff4500", "#00d4ff", "#ffd700", "#22c55e", "#a855f7", "#f97316", "#06b6d4", "#ec4899", "#84cc16", "#14b8a6", "#f43f5e", "#8b5cf6"];
</script>

<div class="screen" style="overflow-y:auto; padding-bottom:60px;">
  <button class="btn btn-ghost" on:click={goBack}>← Back</button>

  {#if view === 'list'}
    <div style="display:flex; justify-content:space-between; align-items:center; margin-top:10px;">
      <h1>🛡️ Manage Teams</h1>
      <button class="btn btn-primary" style="padding:6px 12px; font-size:13px;" on:click={() => { resetForm(); view = 'create'; }}>+ Create Team</button>
    </div>

    <!-- CUSTOM TEAMS -->
    <h3 style="margin-top:16px; color:#fbbf24; border-bottom:1px solid #334155; padding-bottom:6px;">Your Custom Teams</h3>
    {#if customTeams.length === 0}
      <p class="muted" style="font-size:12px; text-align:center; padding: 20px 0;">No custom teams created yet. Click "+ Create Team" to add your own.</p>
    {:else}
      {#each customTeams as t, i_each (t.id || t.name || i_each)}
        <div class="card" style="margin-top:10px; display:flex; justify-content:space-between; align-items:center;">
          <div style="display:flex; align-items:center; gap:12px;">
            <img src={t.logo} class="team-logo-lg" style="width:40px; height:40px; object-fit:contain; background:#1e293b; border-radius:6px; border:1px solid {t.color}88" alt="logo" />
            <div>
              <div style="font-weight:800; color:{t.color}; font-size:15px;">{t.name}</div>
              <div class="meta" style="font-size:11px;">👑 Captain: {t.captain} · 🛡️ {t.players?.length || 0} Players</div>
            </div>
          </div>
          <div style="display:flex; gap:6px;">
            <button class="btn btn-ghost" style="padding:4px 8px; font-size:12px;" on:click={() => editTeam(t)}>✏️</button>
            <button class="btn btn-ghost" style="padding:4px 8px; font-size:12px;" on:click={() => deleteTeam(t.id)}>🗑</button>
          </div>
        </div>
      {/each}
    {/if}

    <!-- PRE-LOADED TEAMS (READ ONLY) -->
    <h3 style="margin-top:24px; color:#9ca3af; border-bottom:1px solid #334155; padding-bottom:6px;">Default League Teams (Read-Only)</h3>
    {#each $availableTeams.filter(t => !customTeams.some(ct => ct.name === t.name)) as t, i_each (t.id || t.name || i_each)}
      <div class="card" style="margin-top:10px; opacity:0.85; display:flex; align-items:center; gap:12px;">
        <img src={t.logo} class="team-logo-lg" style="width:40px; height:40px; object-fit:contain;" alt="logo" />
        <div>
          <div style="font-weight:800; color:{t.color}; font-size:14px;">{t.name}</div>
          <div class="meta" style="font-size:11px;">👑 Captain: {t.captain}</div>
        </div>
      </div>
    {/each}

  {:else}
    <!-- CREATE OR EDIT FORM -->
    <h1 style="margin-top:10px;">{editId ? 'Edit Team' : 'Create Team'}</h1>

    <div class="card">
      <div class="field">
        <label for="team-name-input">Team Name</label>
        <input type="text" id="team-name-input" bind:value={name} placeholder="e.g. Rohini Royals" style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;" />
      </div>

      <div class="field" style="margin-top:12px;">
        <label for="team-cap-input">Captain Name</label>
        <input type="text" id="team-cap-input" bind:value={captain} placeholder="e.g. Rohit Sharma" style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;" />
      </div>

      <div class="field" style="margin-top:12px;">
        <label for="team-logo-upload">Team Logo</label>
        <div style="display:flex; align-items:center; gap:12px;">
          {#if logo}
            <img src={logo} style="width:50px; height:50px; object-fit:contain; background:#0f172a; border:1px solid #334155; border-radius:8px;" alt="preview" />
          {/if}
          <input type="file" id="team-logo-upload" accept="image/*" on:change={handleLogoUpload} style="font-size:12px; color:#9ca3af;" />
        </div>
      </div>

      <!-- Theme Color -->
      <div class="field" style="margin-top:12px;">
        <label for="team-color-picker">Theme Color</label>
        <div style="display:flex; align-items:center; gap:8px;">
          <input type="color" id="team-color-picker" bind:value={color} style="width:40px; height:40px; border:none; background:none; cursor:pointer;" />
          <div style="display:flex; flex-wrap:wrap; gap:4px; flex:1;">
            {#each presetColors as pc, i_each (pc.id || pc.name || i_each)}
              <button class="color-dot" aria-label="Select theme color {pc}" style="background:{pc}; width:24px; height:24px; border:none; border-radius:50%; cursor:pointer; outline:{color === pc ? '2px solid white' : 'none'}" on:click={() => color = pc}></button>
            {/each}
          </div>
        </div>
      </div>
    </div>

    <!-- PLAYER ROSTER -->
    <div class="card" style="margin-top:12px;">
      <h3 style="margin-top:0;">🛡️ Team Roster (Player Names)</h3>
      <p class="muted" style="font-size:11px; margin-top:-6px; margin-bottom:12px;">Pre-save player names. These will auto-fill at match start.</p>
      
      <div style="display:flex; flex-direction:column; gap:6px;">
        {#each players as p, i (p || i)}
          <div style="display:flex; align-items:center; gap:8px;">
            <div style="width:24px; color:#fbbf24; font-weight:800; text-align:center;">{i === 0 ? '👑' : i + 1}</div>
            <input type="text" bind:value={players[i]} placeholder={i === 0 ? "Captain / Player 1" : "Player " + (i + 1)} style="flex:1; padding:8px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px; font-size:14px;" />
            <button class="btn btn-ghost" style="padding:6px 10px; font-size:12px;" disabled={players.length <= 2} on:click={() => removePlayerSlot(i)}>✕</button>
          </div>
        {/each}
      </div>

      <button class="btn btn-ghost btn-block" style="margin-top:10px; font-size:12px;" on:click={addPlayerSlot}>+ Add Player Slot</button>
    </div>

    <button class="btn btn-primary btn-block" style="margin-top:16px;" on:click={saveTeam}>Save Team & Roster</button>
  {/if}
</div>
